import React, { useState } from "react";
import styles from "../../assets/scss/Reports/Reports.module.scss";
import DatePicker from "react-datepicker";
import { FaDownload, FaEye, FaPrint } from "react-icons/fa";
import Pagination from "../../Components/Common/Pagination";
import { useGetReportsQuery } from "../../slices/admin/admin";
import { useNavigate } from "react-router-dom";
import PopupWrapper from "../../Components/Common/PopupWrapper";
import InvoiceDesignReports from "./InvoiceDesignReports";
import { BaseURL } from "../../lib/api";
import { capitalize } from "../../utils/capitalize";

const Reports = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const navigate = useNavigate();

    // Filters
    const [paymentStatus, setPaymentStatus] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [source, setSource] = useState(""); // ✅ New filter

    // Print popup
    const [showPrintPopup, setShowPrintPopup] = useState(false);
    const [activeTabs, setActiveTabs] = useState("thermal");
    const [selectedBill, setSelectedBill] = useState(null);

    // ✅ API call with new 'source' param
    const { data, isLoading, isError } = useGetReportsQuery({
        page: currentPage,
        limit,
        paymentStatus,
        paymentMethod,
        fromDate: fromDate ? fromDate.toISOString() : "",
        toDate: toDate ? toDate.toISOString() : "",
        source,
    });

    const reports = data?.data || [];
    const total = data?.totalRecords || data?.total || reports.length;
    const totalPages = Math.ceil(total / limit) || (reports.length > 0 ? 1 : 0);


const handleDownloadPDF = async () => {
  try {
    let token = localStorage.getItem("token");

    // ✅ Clean token if stored with quotes
    if (token?.startsWith('"') && token?.endsWith('"')) {
      token = token.slice(1, -1);
    }

    const params = new URLSearchParams({
      offset: currentPage,
      limit,
    });

    if (paymentStatus) params.append("paymentStatus", paymentStatus);
    if (paymentMethod) params.append("paymentMethod", paymentMethod);
    if (fromDate) params.append("fromDate", fromDate.toISOString());
    if (toDate) params.append("toDate", toDate.toISOString());
    if (source) params.append("source", source);
    params.append("export", "pdf");

    const url = `${BaseURL}/billing/reports?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to download PDF");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "reports.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("PDF Download Error:", error);
  }
};



    return (
        <div className={styles.reportsContainer}>
            <h2 className={styles.title}>Reports</h2>

            {/* Filters */}
            <div className={styles.filterBar}>
                <select
                    className={styles.inputs}
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                >
                    <option value="">Payment Method</option>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="credit">Credit</option>
                </select>

                <select
                    className={styles.inputs}
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                >
                    <option value="">Select Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                </select>

                {/* ✅ New Source Dropdown */}
                <select
                    className={styles.inputs}
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                >
                    <option value="">Select Source</option>
                    <option value="Billing">Receivable  Billing</option>
                    <option value="PayableBill">Payable Bill</option>
                </select>

                <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="From Date"
                    maxDate={new Date()}
                    isClearable
                    className={styles.inputss}
                />

                <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="To Date"
                    maxDate={new Date()}
                    isClearable
                    className={styles.inputss}
                />

                <button
                    className={styles.clearBtn}
                    onClick={() => {
                        setPaymentStatus("");
                        setPaymentMethod("");
                        setFromDate(null);
                        setToDate(null);
                        setSource("");
                        setCurrentPage(1);
                    }}
                >
                    Clear All
                </button>
            </div>

            {/* Table */}
            <div className={styles.tableWrapper}>
                {isLoading ? (
                    <p>Loading...</p>
                ) : isError ? (
                    <p>Error fetching reports</p>
                ) : reports.length === 0 ? (
                    <p className={styles.noData}>No records found</p>
                ) : (
                    <table className={styles.responsiveTable}>
                        <thead>
                            <tr>
                                <th>Receipt No</th>
                                <th>Customer Name</th>
                                <th>Date</th>
                                <th>Payment Method</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Source</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((row) => (
                                <tr key={row._id}>
                                    <td>{row.receiptNo}</td>
                                    <td>{row?.customerId?.firstName ? capitalize(row.customerId.firstName) : '-'}</td>
                                    <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                                    <td>{row.paymentMethod ? capitalize(row.paymentMethod) : "-"}</td>
                                    <td>
                                        <span className={`${styles.status} ${
                                            row.paymentStatus?.toLowerCase() === "completed" || row.paymentStatus?.toLowerCase() === "paid" || row.paymentStatus?.toLowerCase() === "approved"
                                            ? styles.statusApproved
                                            : row.paymentStatus?.toLowerCase() === "pending"
                                            ? styles.statusRejected
                                            : styles.statusDefault
                                        }`}>
                                            {row.paymentStatus ? capitalize(row.paymentStatus) : '-'}
                                        </span>
                                    </td>
                                    <td className="notranslate">{row.totalAmount || "-"}</td>
                                    <td>{row.source || "-"}</td> {/* ✅ show source */}
                                    <td>
                                        <div className={styles.actions}>
                                            <FaEye
                                                className={styles.icon}
                                                onClick={() =>
                                                    navigate(`/dashboard/Viewreports?id=${row._id}`)
                                                }
                                            />
                                            <FaPrint
                                                className={styles.icon}
                                                onClick={() => {
                                                    setSelectedBill(row);
                                                    setShowPrintPopup(true);
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Total Section */}
            <div className={styles.totalAmount}>
                <div className={styles.dflexx}>
                    <div className={styles.dflex}>
                        <span className={styles.label}>Total Amount : </span>
                        <span className={styles.amount}>
                            ₹ {reports.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0)}
                        </span>
                    </div>
                    {/* <div className={styles.dflexs}>
                        <button className={styles.printBtn} onClick={handleDownloadPDF}>
                            Download <FaDownload />
                        </button>
                    </div> */}

                </div>
            </div>

            {/* Pagination */}
            <div className={styles.paginationWrapper}>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
                <select
                    className={styles.inputsbtn}
                    value={limit}
                    onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                >
                    <option value={10}>10 rows</option>
                    <option value={25}>25 rows</option>
                    <option value={50}>50 rows</option>
                </select>
            </div>

            {/* Print Popup */}
            {showPrintPopup && selectedBill && (
                <PopupWrapper onClose={() => setShowPrintPopup(false)}>
                    <div className={styles.popupHeader}>
                        <div className={styles.headers}>
                            <h2 className={styles.tittle}>Share</h2>
                            <span className={styles.closeIcon} onClick={() => setShowPrintPopup(false)}>
                                ✕
                            </span>
                        </div>

                        <div className={styles.tabHeader}>
                            <button
                                className={activeTabs === "thermal" ? styles.activeTabs : ""}
                                onClick={() => setActiveTabs("thermal")}
                            >
                                Thermal Print
                            </button>
                            <button
                                className={activeTabs === "whatsapp" ? styles.activeTabs : ""}
                                onClick={() => setActiveTabs("whatsapp")}
                            >
                                WhatsApp Share
                            </button>
                        </div>

                        <div className={styles.tabContent}>
                            {activeTabs === "thermal" && (
                                <div className={styles.invoicePreview}>
                                    <InvoiceDesignReports mode="thermal" data={selectedBill} />
                                </div>
                            )}
                            {activeTabs === "whatsapp" && (
                                <div className={styles.whatsappShare}>
                                    <InvoiceDesignReports mode="whatsapp" data={selectedBill} />
                                </div>
                            )}
                        </div>
                    </div>
                </PopupWrapper>
            )}
        </div>
    );
};

export default Reports;

